import { Typography } from '@mui/material';
import { Body02, useMediaSize } from 'Components/designSystem';
import { JobStatus } from 'features/myJobs';
import { OfferType } from 'pages/candidateOffer/types';
import { formatSchedulePreviewRange } from 'pages/candidateOfferList';
import { CalendarIcon, EnterpriseIcon, LocationIcon } from 'pages/candidateOfferList/img';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TextWithParsedLinks from 'shared/components/TextWithParsedLinks';
import styled from 'styled-components';

import IconRow from './IconRow';
import ProcessedOfferStatus from './ProcessedOfferStatus';

const COMMENT_LIMIT_MOBILE = 100;
const COMMENT_LIMIT_DESKTOP = 320;

interface OfferDetailsViewProps {
    role?: string;
    customer?: string;
    department?: string;
    location?: string;
    dateStartUtc?: string;
    dateEndUtc?: string;
    informationForCandidate?: string;
    offerType: OfferType;
    status?: JobStatus;
    elementsCount?: number;
    timeZone: string;
    expandableInfoForCandidate?: boolean;
    headerAdornment?: ReactNode;
}

export default function OfferDescription(props: OfferDetailsViewProps) {
    const { t } = useTranslation([ 'dates', 'candidateOffers' ]);
    const { isMobile } = useMediaSize();

    const {
        role,
        customer,
        department,
        location,
        offerType,
        informationForCandidate,
        status,
        timeZone,
        dateStartUtc,
        dateEndUtc,
        elementsCount,
        expandableInfoForCandidate,
        headerAdornment
    } = props;

    const schedulePreview = useMemo(() => {
        const range = formatSchedulePreviewRange(dateStartUtc, dateEndUtc, timeZone, t);

        if (!range) {
            return null;
        }

        const elementsCountStr = offerType === 'Shifts' 
            ? t('candidateOffers:shifts', { count: elementsCount })
            : t('candidateOffers:periods', { count: elementsCount });

        return `${ range }, ${ elementsCountStr }`;
    }, [ dateEndUtc, dateStartUtc, offerType, elementsCount, timeZone, t ]);

    const topLine = useMemo(() => {
        if (!(customer || department)) {
            return undefined;
        }

        if (customer) {
            return `${ customer }${ department ? ` - ${ department }` : '' }`;
        }

        return department;
    }, [ customer, department ]);

    return (
        <MediumGapContainer>
            <SmallGapContainer>
                { (role || status || headerAdornment) && (
                    <HeadingRow>
                        { role && (
                            <Typography fontWeight="medium"
                                flex="1">
                                { role }
                            </Typography>
                        ) }
                        <ProcessedOfferStatus status={ status } />
                        { headerAdornment }
                    </HeadingRow>
                ) }

                <IconRow icon={ <EnterpriseIcon /> }
                    content={ topLine }
                />
                <IconRow icon={ <LocationIcon /> }
                    content={ location }
                />
                { schedulePreview && <IconRow icon={ <CalendarIcon /> }
                    content={ schedulePreview }
                /> }
            </SmallGapContainer>

            { Boolean(informationForCandidate) && (
                <InfoForCandidate>
                    <TextWithParsedLinks text={ informationForCandidate }
                        expandable={ expandableInfoForCandidate }
                        symbolsLimit={ isMobile ? COMMENT_LIMIT_MOBILE : COMMENT_LIMIT_DESKTOP } />
                </InfoForCandidate>
            ) }
        </MediumGapContainer>
    );
}

const SmallGapContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${ ({ theme }) => theme.size.size_10 };
`;

const MediumGapContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${ ({ theme }) => theme.size.size_15 };
`;

const HeadingRow = styled.div`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    column-gap: ${ ({ theme }) => theme.size.size_10 };
`;

export const InfoForCandidate = styled(Body02)`
    white-space: pre-wrap;
`;
